/**
 * Tax, discounts and charges: the `pricing` module's master data (03-modules.md
 * §6, 04-data-model.md "pricing", 05-api.md "Pricing setup").
 *
 * - Tax groups belong to an outlet; each has tax rules: one GST component
 *   (CGST, SGST, IGST or cess) at a rate in basis points (5% = 500), effective
 *   on `[effectiveFrom, effectiveTo)` (`effectiveTo` null = open-ended). Rules of
 *   one component never overlap, CGST and SGST are effective together at the
 *   same rate, and cess is never the only rate of a GST group
 *   (11-open-questions.md Q16). The calculator, `packages/pricing`, takes these
 *   rows as they are (`TaxGroup` / `TaxRule` in its types).
 * - Discounts belong to an outlet: `percent` (`rateBp`, optional `maxPaise` cap)
 *   or `flat` (`amountPaise`), on the whole order, chosen items, or some menu
 *   categories, optionally limited to an active window.
 * - Discount limits belong to a role (company-level): the largest discount a
 *   holder may give without a manager's override (`orders.discount.above_limit`).
 *
 * Charges themselves (packaging, service, delivery) are org's `other_charge`;
 * their `taxGroupId` points at a tax group here. Rounding is the outlet setting
 * `round_off` (org).
 *
 * This file must not import `org.ts` (org's bootstrap bundle imports it).
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { basisPointsSchema, nonNegativePaiseSchema, percentBasisPointsSchema } from './money.js';

const nameSchema = z.string().trim().min(1).max(60);
const sortSchema = z.int().min(0).max(10_000);
const atLeastOne = { message: 'Send at least one field to change' };
const archivable = {
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
};

// ---------------------------------------------------------------------------
// Tax groups and tax rules

/** GST components (`tax_rule.component`). */
export const TAX_RULE_COMPONENTS = ['cgst', 'sgst', 'igst', 'cess'] as const;
export type TaxRuleComponent = (typeof TAX_RULE_COMPONENTS)[number];

/** Most rules one tax group may hold (history included); a guard, not a business rule. */
export const MAX_TAX_RULES_PER_GROUP = 48;

export const taxRuleSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  taxGroupId: uuidSchema,
  component: z.enum(TAX_RULE_COMPONENTS),
  /** Basis points: CGST 2.5% = 250. */
  rateBp: z.int(),
  /** First day the rate applies (`YYYY-MM-DD`, the date of supply in the outlet's timezone). */
  effectiveFrom: businessDateSchema,
  /** The day the next rate starts (exclusive); null = until further notice. */
  effectiveTo: businessDateSchema.nullable(),
  ...archivable,
});
export type TaxRuleView = z.output<typeof taxRuleSchema>;

export const taxGroupSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  /** Its live rules (history included), by component then date. */
  rules: z.array(taxRuleSchema),
  ...archivable,
});
export type TaxGroupView = z.output<typeof taxGroupSchema>;

const effectiveToAfterFrom = (v: {
  effectiveFrom?: string | undefined;
  effectiveTo?: string | null | undefined;
}) =>
  v.effectiveFrom === undefined ||
  v.effectiveTo === undefined ||
  v.effectiveTo === null ||
  v.effectiveTo > v.effectiveFrom;
const effectiveToMessage = {
  message: 'effectiveTo must be after effectiveFrom',
  path: ['effectiveTo'],
};

/** A rule as sent in a tax group's rule set (`PUT {o}/tax-groups/{id}/rules`, create). */
export const taxRuleInputSchema = z
  .object({
    /** Keep a rule's id to change it in place; a new (or no) id adds one. */
    id: uuidSchema.optional(),
    component: z.enum(TAX_RULE_COMPONENTS),
    rateBp: basisPointsSchema,
    effectiveFrom: businessDateSchema,
    effectiveTo: businessDateSchema.nullable().optional(),
  })
  .refine(effectiveToAfterFrom, effectiveToMessage);
export type TaxRuleInput = z.output<typeof taxRuleInputSchema>;

const ruleSetSchema = z
  .array(taxRuleInputSchema)
  .max(MAX_TAX_RULES_PER_GROUP)
  .refine(
    (rules) => {
      const ids = rules.flatMap((r) => (r.id === undefined ? [] : [r.id]));
      return new Set(ids).size === ids.length;
    },
    { message: 'Each rule id at most once' },
  );

/** `POST {o}/tax-groups`: a name and, optionally, its rules (CGST + SGST together). */
export const createTaxGroupSchema = z.object({
  id: uuidSchema.optional(),
  name: nameSchema,
  rules: ruleSetSchema.optional(),
});

/** `PATCH {o}/tax-groups/{id}`: rename (rates change through the rules). */
export const updateTaxGroupSchema = z
  .object({ name: nameSchema })
  .partial()
  .refine((v) => Object.keys(v).length > 0, atLeastOne);

/** `PUT {o}/tax-groups/{id}/rules`: the group's complete rule set, replaced atomically. */
export const setTaxRulesSchema = z.object({ rules: ruleSetSchema });

/** `POST {o}/tax-rules`: one rule of a group. */
export const createTaxRuleSchema = z
  .object({
    id: uuidSchema.optional(),
    taxGroupId: uuidSchema,
    component: z.enum(TAX_RULE_COMPONENTS),
    rateBp: basisPointsSchema,
    effectiveFrom: businessDateSchema,
    effectiveTo: businessDateSchema.nullable().optional(),
  })
  .refine(effectiveToAfterFrom, effectiveToMessage);

/** `PATCH {o}/tax-rules/{id}`: rate and dates (a rule keeps its group and component). */
export const updateTaxRuleSchema = z
  .object({
    rateBp: basisPointsSchema,
    effectiveFrom: businessDateSchema,
    effectiveTo: businessDateSchema.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, atLeastOne)
  .refine(effectiveToAfterFrom, effectiveToMessage);

export const taxGroupParamsSchema = z.object({ outletId: uuidSchema, taxGroupId: uuidSchema });
export const taxRuleParamsSchema = z.object({ outletId: uuidSchema, taxRuleId: uuidSchema });

/** `GET {o}/tax-rules?taxGroupId=&includeArchived=`. */
export const taxRuleListQuerySchema = z.object({
  taxGroupId: uuidSchema.optional(),
  includeArchived: z.enum(['true', 'false']).optional(),
});

// ---------------------------------------------------------------------------
// Discounts

/** What a discount applies to: the whole order, items chosen when applying it, or categories. */
export const DISCOUNT_SCOPES = ['order', 'item', 'category'] as const;
export type DiscountScope = (typeof DISCOUNT_SCOPES)[number];
export const DISCOUNT_CALCS = ['percent', 'flat'] as const;
export type DiscountCalc = (typeof DISCOUNT_CALCS)[number];

/** Most categories one discount may name. */
export const MAX_DISCOUNT_CATEGORIES = 100;

export const discountSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  scope: z.enum(DISCOUNT_SCOPES),
  calc: z.enum(DISCOUNT_CALCS),
  /** `flat`: the amount in paise; null for `percent`. */
  amountPaise: nonNegativePaiseSchema.nullable(),
  /** `percent`: the rate in basis points (10% = 1000); null for `flat`. */
  rateBp: z.int().nullable(),
  /** `percent`: the most it takes off, in paise; null = no cap. Always null for `flat`. */
  maxPaise: nonNegativePaiseSchema.nullable(),
  /** `category` scope: the menu categories it applies to; empty otherwise. */
  categoryIds: z.array(uuidSchema),
  /** The till asks for a reason when applying it. */
  requiresReason: z.boolean(),
  /** Usable from this instant (inclusive); null = no start. */
  activeFrom: isoTimestampSchema.nullable(),
  /** Usable until this instant (exclusive); null = no end. */
  activeTo: isoTimestampSchema.nullable(),
  sort: z.int(),
  ...archivable,
});
export type DiscountView = z.output<typeof discountSchema>;

const categoryIdsSchema = z
  .array(uuidSchema)
  .max(MAX_DISCOUNT_CATEGORIES)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'Each category at most once' });

const activeWindowOk = (v: {
  activeFrom?: string | null | undefined;
  activeTo?: string | null | undefined;
}) =>
  v.activeFrom === undefined ||
  v.activeFrom === null ||
  v.activeTo === undefined ||
  v.activeTo === null ||
  new Date(v.activeTo).getTime() > new Date(v.activeFrom).getTime();
const activeWindowMessage = { message: 'activeTo must be after activeFrom', path: ['activeTo'] };

const discountFields = {
  name: nameSchema,
  scope: z.enum(DISCOUNT_SCOPES),
  calc: z.enum(DISCOUNT_CALCS),
  amountPaise: nonNegativePaiseSchema.nullable(),
  rateBp: percentBasisPointsSchema.nullable(),
  maxPaise: nonNegativePaiseSchema.nullable(),
  categoryIds: categoryIdsSchema,
  requiresReason: z.boolean(),
  activeFrom: isoTimestampSchema.nullable(),
  activeTo: isoTimestampSchema.nullable(),
  sort: sortSchema,
};

/** `POST {o}/discounts`. Consistency (amount vs rate, categories vs scope) is checked by the command. */
export const createDiscountSchema = z
  .object({
    id: uuidSchema.optional(),
    name: discountFields.name,
    scope: discountFields.scope,
    calc: discountFields.calc,
    amountPaise: nonNegativePaiseSchema.optional(),
    rateBp: percentBasisPointsSchema.optional(),
    maxPaise: nonNegativePaiseSchema.optional(),
    categoryIds: categoryIdsSchema.optional(),
    requiresReason: z.boolean().optional(),
    activeFrom: isoTimestampSchema.optional(),
    activeTo: isoTimestampSchema.optional(),
    sort: sortSchema.optional(),
  })
  .refine(activeWindowOk, activeWindowMessage);

/** `PATCH {o}/discounts/{id}`: any subset; `null` clears an optional value. */
export const updateDiscountSchema = z
  .object(discountFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, atLeastOne)
  .refine(activeWindowOk, activeWindowMessage);

export const discountParamsSchema = z.object({ outletId: uuidSchema, discountId: uuidSchema });

// ---------------------------------------------------------------------------
// Discount limits per role

/**
 * A role's discount limit. Within it, a holder of `orders.discount` applies a
 * discount on their own; above it (or with no limit set) the discount needs
 * `orders.discount.above_limit`, i.e. a manager's override.
 *
 * - `maxPercentBp`: a percent discount's rate, or a flat discount's share of the
 *   value it applies to, may not exceed this.
 * - `maxPaise`: no single discount may take off more than this; null = no amount cap.
 */
export const discountLimitSchema = z.object({
  roleId: uuidSchema,
  maxPercentBp: z.int(),
  maxPaise: nonNegativePaiseSchema.nullable(),
  /** False when nobody set a limit for the role: then every discount needs an override. */
  configured: z.boolean(),
  /** 0 while not configured. */
  version: z.int(),
  updatedAt: isoTimestampSchema.nullable(),
});
export type DiscountLimitView = z.output<typeof discountLimitSchema>;

/** `PUT /v1/roles/{id}/discount-limit`. */
export const setDiscountLimitSchema = z.object({
  maxPercentBp: percentBasisPointsSchema,
  maxPaise: nonNegativePaiseSchema.nullable(),
});

/** A configured limit as the till caches it (bootstrap). */
export const roleDiscountLimitSchema = z.object({
  roleId: uuidSchema,
  maxPercentBp: z.int(),
  maxPaise: nonNegativePaiseSchema.nullable(),
});
export type RoleDiscountLimit = z.output<typeof roleDiscountLimitSchema>;

// ---------------------------------------------------------------------------
// Events

export const taxGroupChangedV1 = defineEvent(
  'tax_group.changed',
  1,
  z.object({
    taxGroupId: uuidSchema,
    /** `rules`: its tax rules changed (one rule, or the whole set). */
    change: z.enum(['created', 'updated', 'archived', 'rules']),
    version: z.int(),
  }),
);

export const discountChangedV1 = defineEvent(
  'discount.changed',
  1,
  z.object({
    discountId: uuidSchema,
    change: z.enum(['created', 'updated', 'archived']),
    version: z.int(),
  }),
);

/** Company-level (a role's limit applies at every outlet). */
export const discountLimitChangedV1 = defineEvent(
  'discount_limit.changed',
  1,
  z.object({
    roleId: uuidSchema,
    maxPercentBp: z.int(),
    maxPaise: nonNegativePaiseSchema.nullable(),
    version: z.int(),
  }),
);

/**
 * A role's discount limit changed, told to one outlet: `pricing.setDiscountLimit`
 * emits one per outlet of the company (besides the company-level
 * `discount_limit.changed`), so the tills of every outlet, whose bootstrap carries
 * the limits, get `setup.changed` (realtime projections are per outlet).
 */
export const outletDiscountLimitsChangedV1 = defineEvent(
  'outlet_discount_limits.changed',
  1,
  z.object({
    roleId: uuidSchema,
    version: z.int(),
  }),
);

/** Pricing events a till's cached bootstrap depends on (realtime `setup.changed`). */
export const PRICING_SETUP_EVENTS = [
  taxGroupChangedV1,
  discountChangedV1,
  outletDiscountLimitsChangedV1,
] as const;

export const PRICING_EVENTS = [...PRICING_SETUP_EVENTS, discountLimitChangedV1] as const;
